import { Button } from '@headlessui/react'
import {
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  Download,
  Trash2,
  CalendarDays,
  FileImage,
  FileAudio,
  FileVideo,
  FileArchive,
  FileText
} from 'lucide-react'
import React from 'react'

import ProgressBar from '@/components/common/ProgressBar'
import type { FileInfoResponse } from '@/services/StorageService'

interface FileCardProps {
  file?: File
  fileInfo?: FileInfoResponse
  status?: 'pending' | 'uploading' | 'success' | 'error'
  progress?: number
  error?: string
  onRemove?: () => void
  onRetry?: () => void
  onUpload?: () => void
  onDownload?: () => void
  onDelete?: () => void
  disabled?: boolean
  isMobile?: boolean
  iconSize?: number // 新增：图标大小控制
}

const FileCard: React.FC<FileCardProps> = ({
  file,
  fileInfo,
  status,
  progress = 0,
  error,
  onRemove,
  onRetry,
  onUpload,
  onDownload,
  onDelete,
  disabled = false,
  isMobile = false,
  iconSize = 32
}) => {
  const fileName =
    file?.name ||
    (fileInfo?.metadata?.original_filename as string) ||
    fileInfo?.key.split('/').pop() ||
    'unknown'
  const fileSize = file?.size || fileInfo?.size || 0
  const lastModified = fileInfo?.last_modified
    ? new Date(fileInfo.last_modified).toLocaleDateString()
    : undefined
  const isExisting = !!fileInfo && !file

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  const getFileIcon = (name: string, type?: string, size: number = 24) => {
    const iconProps = {
      width: size,
      height: size,
      className: `w-${size / 4} h-${size / 4}`
    }

    if (type) {
      if (type.startsWith('image/')) return <FileImage {...iconProps} />
      if (type.startsWith('video/')) return <FileVideo {...iconProps} />
      if (type.startsWith('audio/')) return <FileAudio {...iconProps} />
      if (type.includes('pdf')) return <FileText {...iconProps} />
      if (type.includes('zip') || type.includes('rar'))
        return <FileArchive {...iconProps} />
    }
    if (name.toLowerCase().includes('.pdf')) return <FileText {...iconProps} />
    if (name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/))
      return <FileImage {...iconProps} />
    if (name.toLowerCase().match(/\.(mp4|avi|mov|wmv)$/))
      return <FileVideo {...iconProps} />
    if (name.toLowerCase().match(/\.(mp3|wav|aac|ogg)$/))
      return <FileAudio {...iconProps} />
    if (name.toLowerCase().match(/\.(zip|rar|7z)$/))
      return <FileArchive {...iconProps} />
    return <FileText {...iconProps} />
  }

  const fileIcon = getFileIcon(
    fileName,
    file?.type || fileInfo?.content_type,
    iconSize
  )

  // 操作按钮组件
  const ActionButtons = () => {
    if (isExisting) {
      return (
        <div className="flex gap-2">
          <Button
            onClick={onDownload}
            className={`bg-blue-100 hover:bg-blue-200 flex items-center justify-center rounded-sm flex-1 p-2`}
            title="下载"
          >
            <Download
              className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'} text-blue-600`}
            />
            <span className="text-xs ml-2 text-blue-600">下载</span>
          </Button>
          <Button
            onClick={onDelete}
            className={`bg-red-100 hover:bg-red-200 rounded-sm flex items-center justify-center flex-1 p-2`}
            title="删除"
          >
            <Trash2
              className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'} text-red-600`}
            />
            <span className="text-xs ml-2 text-red-600">删除</span>
          </Button>
        </div>
      )
    }

    return (
      <>
        {status === 'error' && (
          <>
            <AlertCircle className="w-4 h-4 text-red-600" />
            {isMobile && (
              <button
                onClick={onRetry}
                disabled={disabled}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                重试
              </button>
            )}
          </>
        )}
        {status === 'uploading' && (
          <>
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            {isMobile && (
              <span className="text-xs text-blue-600">{progress}%</span>
            )}
          </>
        )}
        {status === 'pending' && onUpload && isMobile && (
          <Button
            onClick={onUpload}
            disabled={disabled}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            上传
          </Button>
        )}
        {status === 'success' && (
          <CheckCircle className="w-4 h-4 text-green-600" />
        )}
        <Button
          onClick={onRemove}
          disabled={disabled}
          className="bg-gray-100 hover:bg-gray-200 rounded-sm flex items-center justify-center text-xs p-2 w-full"
        >
          <X className="w-4 h-4 text-gray-600" />
          <span className="ml-2">取消</span>
        </Button>
      </>
    )
  }

  // 状态显示组件（仅PC端非现有文件显示）
  const StatusDisplay = () => {
    if (isExisting || !status || isMobile) return null

    return (
      <div className="space-y-1">
        {status === 'uploading' && (
          <ProgressBar
            visible={true}
            progress={progress}
            height="sm"
            color="blue"
          />
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            {status === 'success' && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
            {status === 'error' && (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            {status === 'uploading' && (
              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            )}
            <span
              className={`text-xs ${
                status === 'success'
                  ? 'text-green-600'
                  : status === 'error'
                  ? 'text-red-600'
                  : status === 'uploading'
                  ? 'text-blue-600'
                  : 'text-gray-500'
              }`}
            >
              {status === 'success' && '上传成功'}
              {status === 'error' && '上传失败'}
              {status === 'uploading' && `${progress}%`}
              {status === 'pending' && '等待上传'}
            </span>
          </div>

          <div className="flex space-x-1">
            {status === 'error' && (
              <button
                onClick={onRetry}
                disabled={disabled}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                重试
              </button>
            )}
            {status === 'pending' && onUpload && (
              <button
                onClick={onUpload}
                disabled={disabled}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                上传
              </button>
            )}
          </div>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    )
  }

  return (
    <div
      className={`
    ${isMobile ? 'flex space-x-3' : 'relative'}
    border rounded-lg p-3 bg-white flex-col
    ${
      status === 'error'
        ? 'border-red-300 bg-red-50'
        : 'border-gray-200 hover:bg-gray-50'
    }
  `}
    >
      <div className="flex justify-end items-center text-xs text-gray-500">
        <CalendarDays className="w-4 h-4" />{' '}
        {isExisting && <span className="ml-2">{lastModified}</span>}
      </div>

      {/* 文件图标 + 文件信息 */}
      <div className="flex mt-2 mb-2">
        <span className="pr-2 flex-shrink-0 flex items-center">{fileIcon}</span>
        <div className="flex flex-col justify-center min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 truncate">
            {fileName}
          </div>
          <div className="text-xs text-gray-500">
            {formatFileSize(fileSize)}
          </div>
        </div>
      </div>

      {/* PC端状态显示 */}
      <StatusDisplay />
      <ActionButtons />
    </div>
  )
}

export default FileCard
